import { cn } from '@/lib/utils';
import { getAvatarOption, getFallbackAvatar } from '@/lib/avatarCatalog';

/**
 * UserAvatar component - displays user avatar with fallback to initials
 * 
 * @param {Object} props
 * @param {Object} props.user - User object with first_name, last_name, name, email, avatar_url
 * @param {string} props.size - Size variant: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showName - Whether to show the name next to avatar
 * @param {boolean} props.showEmail - Whether to show email below name
 */
export function UserAvatar({ 
  user, 
  size = 'md', 
  className,
  showName = false,
  showEmail = false,
}) {
  // Build display name from first/last name or fallback to name or email
  const firstName = user?.first_name || user?.name?.split(' ')[0] || '';
  const lastName = user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '';
  const displayName = firstName && lastName 
    ? `${firstName} ${lastName}`
    : user?.name || user?.email?.split('@')[0] || 'User';
  
  // Get initials
  const initials = firstName && lastName
    ? `${firstName.charAt(0)}${lastName.charAt(0)}`
    : displayName.charAt(0);

  // Size classes
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
  };

  const configuredAvatar = getAvatarOption(user?.avatar_url);
  const fallbackAvatar = getFallbackAvatar(user?.email || displayName);
  const safeAvatar = configuredAvatar || fallbackAvatar;
  const AvatarIcon = safeAvatar.Icon;

  // EIP-124: restore user-uploaded photo URLs. The previous component
  // rendered <img src={user.avatar_url}> for any URL; the refactor in PR #276
  // dropped that branch and fell through to a deterministic icon, silently
  // losing every uploaded photo. We restore the img branch but gate it on
  // HTTPS-only and use referrerPolicy="no-referrer" to avoid leaking the
  // user's email-derived avatar URL via the Referer header.
  const photoUrl = user?.avatar_url;
  const isUserUploadedPhoto =
    typeof photoUrl === 'string' &&
    photoUrl.startsWith('https://') &&
    !configuredAvatar; // skip when it's an eva-avatar: token

  const avatarElement = isUserUploadedPhoto ? (
    <img
      src={photoUrl}
      alt={`${displayName} avatar`}
      referrerPolicy="no-referrer"
      className={cn(
        'rounded-full object-cover flex-shrink-0 border border-border/60',
        sizeClasses[size],
        className
      )}
    />
  ) : safeAvatar ? (
    <div
      aria-label={`${displayName} avatar`}
      className={cn(
        'rounded-full flex items-center justify-center font-medium flex-shrink-0 border border-border/60',
        safeAvatar.bgClass,
        safeAvatar.textClass,
        sizeClasses[size],
        className
      )}
      title={safeAvatar.label}
    >
      <AvatarIcon className={cn(size === 'xs' ? 'w-3.5 h-3.5' : size === 'sm' ? 'w-4 h-4' : size === 'xl' ? 'w-8 h-8' : 'w-5 h-5')} />
    </div>
  ) : (
    <div
      className={cn(
        'rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium flex-shrink-0',
        sizeClasses[size],
        className
      )}
    >
      {initials.toUpperCase()}
    </div>
  );

  if (!showName) {
    return avatarElement;
  }

  return (
    <div className="flex items-center gap-3">
      {avatarElement}
      <div className="min-w-0">
        <p className="font-medium text-foreground truncate">{displayName}</p>
        {showEmail && user?.email && (
          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Helper function to get user's display name
 */
export function getUserDisplayName(user) {
  const firstName = user?.first_name || user?.name?.split(' ')[0] || '';
  const lastName = user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '';
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return user?.name || user?.email?.split('@')[0] || 'User';
}

/**
 * Helper function to get user's initials
 */
export function getUserInitials(user) {
  const firstName = user?.first_name || user?.name?.split(' ')[0] || '';
  const lastName = user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '';
  
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }
  return (user?.name || user?.email || 'U').charAt(0);
}

export default UserAvatar;
